// © 2026 AttainIP Engineering
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchEnterprises, fetchOwnedCases, fetchUsers, type OwnedCase } from '@/api/queries';
import { api } from '@/api/client';
import { Card } from '@/components/Card';

// Administrative case-ownership rescue (RFC-0510 W02, made usable in W38).
//
// RFC-0168 §15.4 found there is no administrative path to rescue an orphaned
// case: transfer_case requires the *requester* to own it, so a departing
// employee (or a revoked guest) orphans every case they owned, permanently.
// This operator surface looks up what a user owns in ONE organisation and
// reassigns a case to another live user of that same organisation.
//
// ⚠️ WHAT THIS PAGE ASKS FOR, AND WHY IT CHANGED.
// It used to ask for two numbers — "User ID  e.g. 1042" and "Organisation ID" —
// which are the ENGINE's internal ids. Neither appears anywhere in the product,
// and this console works in a different identifier space entirely: its Users
// page searches emails and display names and holds Identity UUIDs. So the
// console could not supply the values its own form demanded, and an operator
// without database access could not use the page at all.
//
// Now it asks for an EMAIL and an ORGANISATION CHOSEN FROM THE ENTERPRISES
// LIST — the two things the console already knows. The email suggestions come
// from the same /v1/users endpoint the Users page uses; no second way to find a
// person was added. The BFF turns the email into an engine user id by an EXACT
// match on the identifier the engine derives for that address in that
// organisation, and refuses rather than guessing — see the route.
//
// The organisation is still shown explicitly at every step. It is required
// because identity is per-organisation — the same person holds rows in several
// organisations under separate identities — and an operator acting on the wrong
// one is the mistake this page is designed against.

type Outcome =
  | { kind: 'reassigned'; message: string }
  | { kind: 'no_op'; message: string }
  | { kind: 'refused'; reason: string | null; message: string }
  | { kind: 'error'; message: string };

interface EnterpriseOrg {
  domain: string;
  activated: boolean;
  org_id: string | null;
  ext_id: string | null;
  name: string;
}

interface Lookup {
  email: string;
  orgExtId: string;
  includeDeleted: boolean;
}

// Suggestions start once the operator has typed enough for the search to mean
// something. Below that every keystroke would ask for a slice of the whole
// estate, which is slow and useless.
const SUGGEST_FROM = 3;

function caseLabel(c: OwnedCase): string {
  return c.name || c.title || `Case #${c.case_id}`;
}

function orgLabel(org: EnterpriseOrg | undefined, extId: string): string {
  return org ? `${org.name} (${org.ext_id})` : extId;
}

function errorMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === 'string') return message;
  }
  if (typeof detail === 'string') return detail;
  return fallback;
}

export function CaseOwnershipPage() {
  const queryClient = useQueryClient();

  const [emailInput, setEmailInput] = useState('');
  const [orgExtIdInput, setOrgExtIdInput] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [lookup, setLookup] = useState<Lookup | null>(null);

  const [selectedCase, setSelectedCase] = useState<OwnedCase | null>(null);
  const [newOwnerInput, setNewOwnerInput] = useState('');
  const [reassigning, setReassigning] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  // The organisation is CHOSEN, not typed — from the same enterprises listing
  // the Enterprises page renders. Its `ext_id` is exactly the handle the engine
  // takes on `owned-cases`, so nothing has to be translated on the way.
  const enterprisesQuery = useQuery({ queryKey: ['enterprises'], queryFn: fetchEnterprises });
  const orgs: EnterpriseOrg[] = useMemo(
    () =>
      ((enterprisesQuery.data?.data as EnterpriseOrg[] | undefined) ?? []).filter(
        (o) => o.activated && !!o.ext_id,
      ),
    [enterprisesQuery.data],
  );
  const orgByExtId = useMemo(
    () => new Map(orgs.map((o) => [o.ext_id as string, o])),
    [orgs],
  );

  // Email suggestions from the console's OWN Users surface. Reuse, not a third
  // search: the operator may still type any address (a guest of the firm is not
  // in every listing), and an address with no engine row refuses with its own
  // reason rather than reading as "owns nothing".
  const suggestionsQuery = useQuery({
    queryKey: ['user-suggestions', emailInput.trim()],
    queryFn: () => fetchUsers({ search: emailInput.trim(), limit: 10 }),
    enabled: emailInput.trim().length >= SUGGEST_FROM,
    staleTime: 30_000,
    retry: false,
  });
  const suggestions: string[] = useMemo(() => {
    const rows = (suggestionsQuery.data?.data as { email?: string }[] | undefined) ?? [];
    return rows.map((r) => r.email).filter((e): e is string => !!e);
  }, [suggestionsQuery.data]);

  const ownedCasesQuery = useQuery({
    queryKey: ['owned-cases', lookup?.email, lookup?.orgExtId, lookup?.includeDeleted],
    queryFn: () => fetchOwnedCases(lookup!.email, lookup!.orgExtId, lookup!.includeDeleted),
    enabled: !!lookup,
    retry: false,
  });

  const handleLookup = (e: React.FormEvent) => {
    e.preventDefault();
    const email = emailInput.trim();
    if (!email || !email.includes('@')) {
      setFormError('Enter the email address of the person to look up.');
      return;
    }
    // The organisation is required — refuse the lookup here rather than sending
    // a request the API would reject anyway.
    if (!orgExtIdInput) {
      setFormError(
        'Choose an organisation — a lookup is always scoped to exactly one.',
      );
      return;
    }
    setFormError(null);
    setSelectedCase(null);
    setNewOwnerInput('');
    setOutcome(null);
    setLookup({ email, orgExtId: orgExtIdInput, includeDeleted });
  };

  const handleReassign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCase || !lookup) return;
    const newOwnerId = Number(newOwnerInput);
    if (!newOwnerInput.trim() || !Number.isInteger(newOwnerId) || newOwnerId <= 0) {
      setOutcome({ kind: 'error', message: 'Enter a valid numeric user ID for the new owner.' });
      return;
    }
    setReassigning(true);
    setOutcome(null);
    try {
      const { data } = await api.put(`/v1/cases/${selectedCase.case_id}/owner`, {
        new_owner_user_id: newOwnerId,
      });
      // The BFF reports the no-op-success (reassigning to the current owner)
      // explicitly, so we say so plainly rather than implying a change.
      setOutcome(
        data.no_op
          ? { kind: 'no_op', message: data.message }
          : { kind: 'reassigned', message: data.message },
      );
      // The reassigned case leaves this user's owned list — refresh it.
      queryClient.invalidateQueries({ queryKey: ['owned-cases'] });
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
        ?.detail;
      if (detail && typeof detail === 'object') {
        // Each typed refusal reason carries its own operator-facing message
        // from the BFF — surface it distinctly, never a generic failure.
        setOutcome({
          kind: 'refused',
          reason: (detail as { reason?: string | null }).reason ?? null,
          message:
            (detail as { message?: string }).message ?? 'The reassignment was refused.',
        });
      } else {
        setOutcome({
          kind: 'error',
          message: errorMessage(err, 'The reassignment could not be completed.'),
        });
      }
    } finally {
      setReassigning(false);
    }
  };

  const cases = ownedCasesQuery.data?.cases ?? [];
  const resolvedUserId = ownedCasesQuery.data?.engine_user_id;
  const lookupOrgLabel = lookup ? orgLabel(orgByExtId.get(lookup.orgExtId), lookup.orgExtId) : '';

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h1 className="text-xl font-bold">Case Ownership</h1>
        <p className="text-sm text-gray-500 mt-1">
          Rescue an orphaned case by reassigning it to another live user of the same
          organisation (RFC-0510). Look up what someone owns in one organisation by their
          email address, then reassign a case.
        </p>
      </div>

      {/* Step 1 — look up what a person owns in a specific organisation */}
      <Card title="1. Look up owned cases" className="mb-6">
        <form onSubmit={handleLookup} className="flex flex-wrap items-end gap-4">
          <div>
            <label htmlFor="email" className="block text-xs text-gray-500 mb-1">
              Email address
            </label>
            <input
              id="email"
              type="text"
              list="case-ownership-user-suggestions"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              className="border rounded px-2 py-1 text-sm w-72"
              placeholder="person@firm.com"
            />
            <datalist id="case-ownership-user-suggestions">
              {suggestions.map((email) => (
                <option key={email} value={email} />
              ))}
            </datalist>
          </div>
          <div>
            <label htmlFor="orgExtId" className="block text-xs text-gray-500 mb-1">
              Organisation
            </label>
            <select
              id="orgExtId"
              value={orgExtIdInput}
              onChange={(e) => setOrgExtIdInput(e.target.value)}
              className="border rounded px-2 py-1 text-sm w-72"
            >
              <option value="">Choose an organisation…</option>
              {orgs.map((org) => (
                <option key={org.ext_id as string} value={org.ext_id as string}>
                  {org.name} ({org.ext_id})
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 pb-1">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              className="rounded"
            />
            Include deleted
          </label>
          <button
            type="submit"
            className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700"
          >
            Look up owned cases
          </button>
        </form>
        {enterprisesQuery.isError && (
          <p className="text-sm text-red-600 mt-3">
            The organisation list could not be loaded, so there is nothing to choose from.
          </p>
        )}
        {formError && <p className="text-sm text-red-600 mt-3">{formError}</p>}
      </Card>

      {/* Step 2 — results, scoped to the organisation asked about */}
      {lookup && (
        <Card
          title={`2. Cases owned by ${lookup.email} in ${lookupOrgLabel}`}
          className="mb-6"
        >
          {ownedCasesQuery.isLoading && <p className="text-gray-500 text-sm">Loading…</p>}
          {ownedCasesQuery.isError && (
            // A refusal is NOT an empty list. "We could not find this person",
            // "we are not configured to ask" and "they own nothing" are three
            // different answers and the BFF words each one itself.
            <p className="text-sm text-red-600">
              {errorMessage(ownedCasesQuery.error, 'Failed to look up owned cases.')}
            </p>
          )}
          {!ownedCasesQuery.isLoading && !ownedCasesQuery.isError && (
            <>
              {resolvedUserId !== undefined && (
                <p className="text-xs text-gray-500 mb-3">
                  Resolved to engine user{' '}
                  <span className="font-mono">{resolvedUserId}</span> in{' '}
                  <span className="font-mono">{lookup.orgExtId}</span>.
                </p>
              )}
              {cases.length === 0 && (
                <p className="text-gray-400 text-sm">
                  {lookup.email} owns no cases in {lookupOrgLabel}.
                </p>
              )}
            </>
          )}
          {cases.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="pb-2 font-medium">Case</th>
                  <th className="pb-2 font-medium">ID</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => (
                  <tr key={c.case_id} className="border-b last:border-0">
                    <td className="py-2">
                      {caseLabel(c)}
                      {c.deleted && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                          deleted
                        </span>
                      )}
                    </td>
                    <td className="py-2 font-mono text-gray-500">{c.case_id}</td>
                    <td className="py-2">{c.status ?? '—'}</td>
                    <td className="py-2 text-right">
                      <button
                        onClick={() => {
                          setSelectedCase(c);
                          setOutcome(null);
                          setNewOwnerInput('');
                        }}
                        className={`text-xs px-2 py-0.5 rounded border ${
                          selectedCase?.case_id === c.case_id
                            ? 'border-blue-400 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        {selectedCase?.case_id === c.case_id ? 'Selected' : 'Reassign'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* Step 3 — reassign the selected case, org shown again explicitly */}
      {selectedCase && lookup && (
        <Card title={`3. Reassign "${caseLabel(selectedCase)}" (case ${selectedCase.case_id})`}>
          <p className="text-sm text-gray-600 mb-3">
            Reassign within organisation{' '}
            <span className="font-mono">{lookup.orgExtId}</span>. The new owner must be a
            live user of that same organisation.
          </p>
          <form onSubmit={handleReassign} className="flex flex-wrap items-end gap-4">
            <div>
              <label htmlFor="newOwnerId" className="block text-xs text-gray-500 mb-1">
                New owner user ID
              </label>
              <input
                id="newOwnerId"
                type="text"
                inputMode="numeric"
                value={newOwnerInput}
                onChange={(e) => setNewOwnerInput(e.target.value)}
                className="border rounded px-2 py-1 text-sm w-40"
                placeholder="e.g. 2087"
              />
            </div>
            <button
              type="submit"
              disabled={reassigning}
              className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {reassigning ? 'Reassigning…' : 'Reassign case'}
            </button>
          </form>

          {outcome && (
            <div
              role="status"
              className={`mt-4 text-sm rounded px-3 py-2 ${
                outcome.kind === 'reassigned'
                  ? 'bg-green-50 text-green-700'
                  : outcome.kind === 'no_op'
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-red-50 text-red-600'
              }`}
            >
              {outcome.message}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
